/**
 * LevelEventSystem — Manages random per-level event modifiers.
 *
 * Each level (3+) has a 20% chance to activate a random event that changes
 * gameplay for that level only. Events affect fog, food, coins, bats, etc.
 *
 * Events:
 *  - "Vorath's Hunger":         Food lifetime halved
 *  - "Threia's Light":          Fog reveal radius increased (+2)
 *  - "The Silent Maze":         No bats, no narrator
 *  - "Gold Rush":               3x coin spawns, no food
 *  - "The Labyrinth Remembers": Visited fog re-darkens over time
 */

import {
  LevelEvent,
  LEVEL_EVENTS_POOL,
  Events,
} from '../utils/constants.js';

import {
  LEVEL_EVENT_MIN_LEVEL,
  LEVEL_EVENT_CHANCE,
  LEVEL_EVENT_FOG_RE_DARKEN_DELAY,
  LEVEL_EVENT_FOG_RE_DARKEN_RATE,
  FOG_REVEAL_RADIUS,
} from '../config/game-config.js';

export default class LevelEventSystem {
  /**
   * @param {object} params
   * @param {Phaser.Scene} params.scene - The GameScene instance
   * @param {Phaser.Events.EventEmitter} params.eventBus - Shared event bus
   */
  constructor({ scene, eventBus }) {
    /** @type {Phaser.Scene} */
    this._scene = scene;
    /** @type {Phaser.Events.EventEmitter} */
    this._eventBus = eventBus;
    /** @type {string} Current active event (LevelEvent enum value) */
    this._activeEvent = LevelEvent.NONE;
    /** @type {Phaser.Time.TimerEvent|null} Re-darken timer for LABYRINTH_REMEMBERS */
    this._reDarkenTimer = null;
    /** @type {boolean} Whether re-darken delay has passed */
    this._reDarkenActive = false;
    /** @type {Phaser.Time.TimerEvent|null} Re-darken initial delay timer */
    this._reDarkenDelayTimer = null;
  }

  /**
   * Roll for a level event. Call at the start of each level build.
   * @param {number} level - Current level number
   * @returns {string} The selected LevelEvent (or LevelEvent.NONE)
   */
  rollForEvent(level) {
    this.cleanup();

    if (level < LEVEL_EVENT_MIN_LEVEL) {
      this._activeEvent = LevelEvent.NONE;
      return this._activeEvent;
    }

    if (Math.random() < LEVEL_EVENT_CHANCE) {
      const idx = Math.floor(Math.random() * LEVEL_EVENTS_POOL.length);
      this._activeEvent = LEVEL_EVENTS_POOL[idx];
    } else {
      this._activeEvent = LevelEvent.NONE;
    }

    if (this._activeEvent !== LevelEvent.NONE) {
      this._eventBus.emit(Events.LEVEL_EVENT_ACTIVATED, this._activeEvent);
    }

    return this._activeEvent;
  }

  /**
   * Set a specific event (used when restoring from save).
   * @param {string} eventType - LevelEvent enum value
   */
  setEvent(eventType) {
    this.cleanup();
    this._activeEvent = eventType || LevelEvent.NONE;

    if (this._activeEvent !== LevelEvent.NONE) {
      this._eventBus.emit(Events.LEVEL_EVENT_ACTIVATED, this._activeEvent);
    }

    // Restart re-darken system if restoring that event
    if (this._activeEvent === LevelEvent.LABYRINTH_REMEMBERS) {
      this._startReDarkenSystem();
    }
  }

  /**
   * Get the currently active event.
   * @returns {string}
   */
  getActiveEvent() {
    return this._activeEvent;
  }

  /**
   * Check if a specific event is active.
   * @param {string} eventType
   * @returns {boolean}
   */
  isActive(eventType) {
    return this._activeEvent === eventType;
  }

  /**
   * Check if any event is active.
   * @returns {boolean}
   */
  hasActiveEvent() {
    return this._activeEvent !== LevelEvent.NONE;
  }

  /**
   * Start the "Labyrinth Remembers" fog re-darken system.
   * Called after level build completes and player can move.
   */
  startReDarkenIfNeeded() {
    if (this._activeEvent === LevelEvent.LABYRINTH_REMEMBERS) {
      this._startReDarkenSystem();
    }
  }

  /**
   * Internal: sets up the re-darken delay + periodic timer.
   * @private
   */
  _startReDarkenSystem() {
    this._reDarkenActive = false;

    // Initial delay before fog starts re-darkening
    this._reDarkenDelayTimer = this._scene.time.delayedCall(
      LEVEL_EVENT_FOG_RE_DARKEN_DELAY,
      () => {
        this._reDarkenActive = true;
        // Periodic re-darken tick
        this._reDarkenTimer = this._scene.time.addEvent({
          delay: LEVEL_EVENT_FOG_RE_DARKEN_RATE,
          callback: this._reDarkenTick,
          callbackScope: this,
          loop: true,
        });
      }
    );
  }

  /**
   * Re-darken tick: mark some visited cells as unvisited so fog returns.
   * Cells within the player's current reveal radius are not affected.
   * @private
   */
  _reDarkenTick() {
    const scene = this._scene;
    if (!scene || !scene.visited || !scene.player) return;

    const playerCol = scene.player.gridCol;
    const playerRow = scene.player.gridRow;
    const revealRadius = FOG_REVEAL_RADIUS;
    const visited = scene.visited;

    // Pick a random batch of visited cells to re-darken (2-4 cells per tick)
    const reDarkenCount = 2 + Math.floor(Math.random() * 3);
    let darkened = 0;

    for (let attempts = 0; attempts < 50 && darkened < reDarkenCount; attempts++) {
      const r = Math.floor(Math.random() * visited.length);
      const c = Math.floor(Math.random() * (visited[0]?.length || 0));

      if (!visited[r] || !visited[r][c]) continue;

      // Don't re-darken cells in the player's current reveal radius
      const dist = Math.abs(r - playerRow) + Math.abs(c - playerCol);
      if (dist <= revealRadius) continue;

      visited[r][c] = 0;
      darkened++;
    }

    // Request fog redraw if any cells were darkened
    if (darkened > 0 && scene._drawFullFog) {
      scene._drawFullFog();
    }
  }

  /**
   * Serialize the current event for save state.
   * @returns {string} The active event type string
   */
  serialize() {
    return this._activeEvent;
  }

  /**
   * Pause re-darken timers (for ESC pause).
   */
  pause() {
    if (this._reDarkenTimer) this._reDarkenTimer.paused = true;
    if (this._reDarkenDelayTimer) this._reDarkenDelayTimer.paused = true;
  }

  /**
   * Resume re-darken timers.
   */
  resume() {
    if (this._reDarkenTimer) this._reDarkenTimer.paused = false;
    if (this._reDarkenDelayTimer) this._reDarkenDelayTimer.paused = false;
  }

  /**
   * Cleanup all timers and reset state.
   */
  cleanup() {
    if (this._reDarkenTimer) {
      this._reDarkenTimer.destroy();
      this._reDarkenTimer = null;
    }
    if (this._reDarkenDelayTimer) {
      this._reDarkenDelayTimer.destroy();
      this._reDarkenDelayTimer = null;
    }
    this._reDarkenActive = false;

    if (this._activeEvent !== LevelEvent.NONE) {
      this._eventBus.emit(Events.LEVEL_EVENT_DEACTIVATED, this._activeEvent);
    }
    this._activeEvent = LevelEvent.NONE;
  }
}
